import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { businessInfoAPI } from '../api';
import { useAppStore } from '../stores/useAppStore';
import { useAuth } from './AuthContext';


/**
 * TODO requires a but optimization
 */
const DiscountApprovalContext = createContext();

export const useDiscountApproval = () => {
  const context = useContext(DiscountApprovalContext);
  if (!context) {
    throw new Error('useDiscountApproval must be used within a DiscountApprovalProvider');
  }
  return context;
};

export const DiscountApprovalProvider = ({ children }) => {
  const { user } = useAuth();
  const { addNotification } = useAppStore();
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingDiscount, setPendingDiscount] = useState(null);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isRequestingOTP, setIsRequestingOTP] = useState(false);
  const [approvalRequestId, setApprovalRequestId] = useState(null);
  const modalResolveRefsHolder = useRef([]);

  // Get discount limits for current user from app store
  const getDiscountLimits = useCallback(() => {
    // Get discount limit directly from user object in app store
    const discountLimit = user?.discount_limit || 0;
    
    return {
      user_id: user?.id || 0,
      discount_limit: discountLimit,
      user_role: user?.role?.code || 'cashier',
      
    };
  }, [user]);

  // Check if discount exceeds user's limit
  const checkDiscountLimit = useCallback((discountPercentage) => {
    const limits = getDiscountLimits();
    return discountPercentage <= limits.discount_limit;
  }, [getDiscountLimits]);

  // Request discount approval
  const requestDiscountApproval = useCallback(async (discountData) => {
    try {
      setIsRequestingOTP(true);
      
      const payload = {
        requested_discount_percentage: Number(discountData?.percentage) || 0,
        reason: (discountData?.reason || '').trim() || 'Discount approval requested',
        user_id: user?.id,
        sale_id: discountData?.saleId || null,
        sale_summary: discountData?.saleSummary || undefined,
      };

      const response = await businessInfoAPI.requestDiscountApproval(payload);
      const data = response.data;
      
      setApprovalRequestId(data.approval_request_id);
      setOtpSent(true);
      
      addNotification({
        type: 'success',
        message: 'OTP sent to notification email'
      });
      
      return data;
    } catch (error) {
      console.error('Error requesting discount approval:', error);
      addNotification({
        type: 'error',
        message: error.response?.data?.detail || 'Failed to send OTP'
      });
      throw error;
    } finally {
      setIsRequestingOTP(false);
    }
  }, [user?.id, addNotification]);

  // Verify OTP
  const verifyOTP = useCallback(async () => {
    if (!otpCode || !approvalRequestId) {
      addNotification({
        type: 'error',
        message: 'Please enter OTP code'
      });
      return false;
    }

    try {
      setIsVerifying(true);
      
      const payload = {
        otp_code: otpCode,
        approval_request_id: approvalRequestId
      };

      const response = await businessInfoAPI.verifyOTP(payload);
      const data = response.data;
      
      if (data.verified && data.approval_granted) {
        addNotification({
          type: 'success',
          message: 'Discount approved successfully'
        });
        
        // Call the approval callback first
        if (pendingDiscount?.onApproved) {
          pendingDiscount.onApproved();
        }
        
        // Close modal and reset state
        closeModal();

        modalResolveRefsHolder.current.forEach(resolve => resolve(true));
        
        return true;
      } else {
        addNotification({
          type: 'error',
          message: 'Invalid OTP code'
        });
        return false;
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
      addNotification({
        type: 'error',
        message: error.response?.data?.detail || 'Failed to verify OTP'
      });
      return false;
    } finally {
      setIsVerifying(false);
    }
  }, [otpCode, approvalRequestId, pendingDiscount, addNotification]);

  // Open discount approval modal
  const openDiscountApproval = useCallback((discountData) => {
    setPendingDiscount(discountData);
    setIsModalOpen(true);
    setOtpSent(false);
    setOtpCode('');
    setApprovalRequestId(null);
  }, []);

  

  // The limit that applies right now. An admin may have changed the person's or their
  // role's limit since this page loaded, so ask the server; if it doesn't answer
  // quickly, use the limit loaded at sign-in.
  const currentDiscountLimit = useCallback(async () => {
    try {
      const response = await businessInfoAPI.getDiscountLimits({ timeout: 4000 });
      const live = Number(response.data?.discount_limit);
      if (Number.isFinite(live)) return live;
    } catch {
      // fall through to the limit from sign-in
    }
    return getDiscountLimits().discount_limit;
  }, [getDiscountLimits]);

  // Promise-based discount approval flow
  const discountApprovalFlowLauncher = useCallback(async (discountPercentage, saleSummary = null) => {
    const requested = Number(discountPercentage) || 0;
    if (requested <= 0 || requested <= (await currentDiscountLimit())) {
      // No approval needed
      return { approved: true, reason: '' };
    }

    return new Promise((resolve) => {
      openDiscountApproval({ percentage: discountPercentage, saleSummary });
      modalResolveRefsHolder.current.push(resolve);
    });
  }, [currentDiscountLimit, openDiscountApproval]);
  

  // Close modal and reset state
  const closeModal = useCallback((wasRejected = false, reason = '') => {
    if(wasRejected) {
      modalResolveRefsHolder.current.forEach(resolve => resolve(false));
    } 
    // Call rejection callback if modal was closed without approval
    if (wasRejected && pendingDiscount?.onRejected) {
      pendingDiscount.onRejected();
    }
    
    setIsModalOpen(false);
    setPendingDiscount(null);
    setOtpSent(false);
    setOtpCode('');
    setApprovalRequestId(null);
    setIsVerifying(false);
    setIsRequestingOTP(false);
  }, [pendingDiscount]);

  const value = {
    // State
    isModalOpen,
    pendingDiscount,
    otpSent,
    otpCode,
    isVerifying,
    isRequestingOTP,
    approvalRequestId,
    
    // Actions
    openDiscountApproval,
    closeModal,
    requestDiscountApproval,
    verifyOTP,
    getDiscountLimits,
    checkDiscountLimit,
    discountApprovalFlowLauncher,
    
    // Setters
    setOtpCode
  };

  return (
    <DiscountApprovalContext.Provider value={value}>
      {children}
    </DiscountApprovalContext.Provider>
  );
};

export default DiscountApprovalContext;
